import { IScope, IPromise } from "angular";
import { IModalService, IModalScope } from "angular-ui-bootstrap";
import { IStateInit } from "@gtmhub/core/routing";
import colors from "wwwroot/json/colors.json";
import { IIndicator } from "@gtmhub/error-handling";
import { IOkrGridColumn } from "@gtmhub/goals/models";
import {
  ICustomField,
  CustomFieldActions,
  DropdownCustomFieldPredefinedValue,
  CustomFieldPredefinedValue,
  CustomFieldVisualizationKind,
  MultiSelectCustomFieldPredefinedValue,
  ICustomFieldsStoreState,
} from "@gtmhub/customFields";
import { SelectedSessionsForCustomField, ISessionTag, CustomFieldTargetType } from "@gtmhub/customFields/models/models";
import { INgRedux } from "@gtmhub/state-management";
import { generateCustomFieldNameFromLabel, getDefaultPredefinedValueColor } from "@gtmhub/customFields/utils";
import { defaultOkrGridColumns } from "@gtmhub/goals/utils";
import { IPlanningSession, ISessionsStoreState, PlanningSessionsActions } from "@gtmhub/sessions";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { AssigneeType } from "@gtmhub/assignees";
import { ICustomFieldsScope } from "./custom-fields";

const bindStateToCustomFieldScope = (state: ICustomFieldsStoreState & ISessionsStoreState): Record<string, unknown> => {
  return {
    customFields: Object.values(state.customFields.map).reduce((customFields, typeCfArr) => ((customFields = [...customFields, ...typeCfArr]), customFields), []),
    sessions: state.sessions.map,
    indicators: {
      getting: state.customFields.fetching,
      creating: state.customFields.creating,
      updating: state.customFields.updating,
      gettingSessions: state.sessions.isFetching,
    },
  };
};

const GLOBAL = "global";
const SESSION = "session";

export interface ICustomFieldScope extends IScope, IModalScope, IExtendedRootScopeService {
  maxLabelLength: number;
  maxInputLength: number;

  customField: ICustomField;
  originalCustomField: ICustomField;

  colors: string[];
  indicators: {
    getting?: IIndicator;
    creating?: IIndicator;
    updating?: IIndicator;
    gettingSessions?: IIndicator;
  };
  mode: string;
  editingCustomFields: boolean;
  descriptionToggled: boolean;
  showVisualizationKindOptionsErrorMessage: boolean;
  showSessionSpecificErrorMessage: boolean;

  sessions: IPlanningSession[];
  sessionTags: ISessionTag[];
  sessionSpecific: {
    enabled: boolean;
  };
  sessionListView: {
    expanded: boolean;
  };

  save(): void;
  addPredefinedValue(): void;
  removePredefinedValue(index: number);
  checkVisualizationKind(visualizationKind: CustomFieldVisualizationKind): void;
  checkForReservedNames(customFieldName: string): boolean;
  resetIndicator(indicator: keyof ICustomFieldsScope["indicators"]): void;
  setVisualizationKind(visualizationKind: CustomFieldVisualizationKind): void;
  setTargetType(targetType: CustomFieldTargetType): void;
  isSessionSpecificCompatible(): boolean;
  countInputLength(fieldLength: number): number;
  isOptionAvailable(visualizationKind: CustomFieldVisualizationKind): boolean;
  isFormValid(): boolean;
  openSpecifySessionModal(): void;
  setGlobalCustomSessionProperties(): void;
  shouldDisableActionButton(): boolean;
  setOperationalType(operationalType: string): void;
  setTargetGroup(targetGroup: AssigneeType): void;
  clearVisualizationKindOptionsErrorMessage(): void;
  clearSessionSpecificErrorMessage(): void;
}

export interface ICustomFieldParams {
  id: string;
}

export class CustomFieldCtrl implements IStateInit {
  private defaultColumns: IOkrGridColumn[];
  public static $inject = ["$scope", "CustomFieldActions", "PlanningSessionsActions", "$ngRedux", "$uibModal", "$rootScope", "paramId"];

  constructor(
    private $scope: ICustomFieldScope,
    private customFieldActions: CustomFieldActions,
    private planningSessionsActions: PlanningSessionsActions,
    private $ngRedux: INgRedux,
    private $uibModal: IModalService,
    private $rootScope: ITraceRootScopeService,
    private paramId: string
  ) {
    this.$scope.maxLabelLength = 45;
    this.$scope.maxInputLength = 255;

    this.$scope.editingCustomFields = !!this.paramId;
    this.$scope.colors = colors.customFieldColors;
    this.defaultColumns = defaultOkrGridColumns();
    this.$scope.indicators = {};
    this.$scope.descriptionToggled = false;
    this.$scope.sessionTags = [];
    this.$scope.showVisualizationKindOptionsErrorMessage = false;
    this.$scope.showSessionSpecificErrorMessage = false;

    this.$scope.addPredefinedValue = this.addPredefinedValue;
    this.$scope.removePredefinedValue = this.removePredefinedValue;
    this.$scope.checkVisualizationKind = this.checkVisualizationKind;
    this.$scope.save = this.save;
    this.$scope.resetIndicator = this.resetIndicator;
    this.$scope.shouldDisableActionButton = this.shouldDisableActionButton;
    this.$scope.setOperationalType = this.setOperationalType;
    this.$scope.setVisualizationKind = this.setVisualizationKind;
    this.$scope.setTargetType = this.setTargetType;
    this.$scope.isSessionSpecificCompatible = this.isSessionSpecificCompatible;
    this.$scope.countInputLength = this.countInputLength;
    this.$scope.isOptionAvailable = this.isOptionAvailable;
    this.$scope.isFormValid = this.isFormValid;
    this.$scope.openSpecifySessionModal = this.openSpecifySessionModal;
    this.$scope.setGlobalCustomSessionProperties = this.setGlobalCustomSessionProperties;
    this.$scope.setTargetGroup = this.setTargetGroup;
    this.$scope.clearVisualizationKindOptionsErrorMessage = this.clearVisualizationKindOptionsErrorMessage;
    this.$scope.clearSessionSpecificErrorMessage = this.clearSessionSpecificErrorMessage;

    const unsubscribe = this.$ngRedux.connect(bindStateToCustomFieldScope)(this.$scope);
    const unsubscribeCustomFields = this.$scope.$watch("customFields", (newValue: ICustomField[]) => {
      if (newValue?.length && this.paramId) {
        this.$scope.originalCustomField = newValue.find((customField) => customField._id === this.paramId);
        this.$scope.customField = angular.copy(this.$scope.originalCustomField);
        unsubscribeCustomFields();
      }
    });

    this.$scope.$on("$destroy", unsubscribe);
    this.$scope.$on("$destroy", unsubscribeCustomFields);
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private init(): IPromise<unknown> {
    this.$scope.sessionListView = {
      expanded: false,
    };

    if (this.paramId) {
      this.$scope.mode = "edit";
      this.$ngRedux.dispatch(this.customFieldActions.getCustomFields());
      this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
      return this.$scope.watchUntil(() => !this.$scope.indicators.gettingSessions).then(() => this.createSessionTagsWhenEditingCustomField());
    } else {
      this.$scope.mode = "create";

      this.$scope.sessionSpecific = {
        enabled: false,
      };

      this.$scope.customField = {
        name: "",
        description: "",
        tooltip: "",
        filterable: false,
        label: "",
        // sets initial value of target type in dropdown menu in a custom field
        targetTypes: ["goal"],
        type: "string",
        visualizationKind: "gtmhub.text",
        settings: {
          predefinedValues: [],
          validations: {
            required: false,
          },
        },
        operationalType: GLOBAL,
        targetIds: [],
      };
    }
  }

  private shouldDisableActionButton = (): boolean => {
    const changeInProgress = this.$scope.editingCustomFields ? !!this.$scope.indicators.updating : !!this.$scope.indicators.creating;
    const areReservedNamesUsed = this.checkForReservedNames(this.$scope.customField.name);

    return changeInProgress || !this.$scope.customField.label || this.$scope.customField.label.length == 0 || areReservedNamesUsed;
  };

  private createSessionTagsWhenEditingCustomField = (): void => {
    if (this.$scope.originalCustomField && this.$scope.customField.operationalType) {
      if (this.$scope.customField.operationalType === SESSION && this.$scope.customField.targetIds && this.$scope.customField.targetIds.length > 0) {
        this.$scope.sessionSpecific = {
          enabled: true,
        };

        this.$scope.sessionTags = this.$scope.customField.targetIds.reduce((sessionTags: ISessionTag[], sessionId) => {
          if (this.$scope.sessions[sessionId] && this.$scope.sessions[sessionId].id) {
            const sessionTag: ISessionTag = {
              id: this.$scope.sessions[sessionId].id,
              name: this.$scope.sessions[sessionId].title,
              color: this.$scope.sessions[sessionId].color,
            };
            sessionTags.push(sessionTag);
          }
          return sessionTags;
        }, []);
      }
      if (this.$scope.customField.operationalType === GLOBAL && (!this.$scope.customField.targetIds || this.$scope.customField.targetIds.length == 0)) {
        this.$scope.sessionSpecific = {
          enabled: false,
        };
      }
    } else {
      this.$scope.sessionSpecific = {
        enabled: false,
      };
    }
  };

  private checkVisualizationKind = (visualizationKind: CustomFieldVisualizationKind): void => {
    this.$scope.customField.settings.predefinedValues = [];

    if (visualizationKind === "gtmhub.selectassignee") {
      this.$scope.customField.settings.predefinedValues = {
        targetGroup: "both",
      };
    }

    // sets data type corresponding to visualizationKind
    this.$scope.customField.type = this.getVisualizationKindDataType(visualizationKind);
  };

  private getVisualizationKindDataType = (visualizationKind: string): string => {
    // maps visualizationKind to dataType
    switch (visualizationKind) {
      case "gtmhub.selectassignee":
        return "[]string";

      case "gtmhub.multiselect":
        return "[]string";

      case "gtmhub.datepicker":
        return "date";

      default:
        return "string";
    }
  };

  private isOptionAvailable = (visualizationKind: string): boolean => {
    switch (this.$scope.mode) {
      case "create":
        return true;
      case "edit":
        return this.$scope.customField.type === this.getVisualizationKindDataType(visualizationKind);
    }
  };

  private save = (): void => {
    const unsubscribe = this.$scope.$watch("indicators", (after: Record<string, unknown>, before: Record<string, Record<string, unknown>>) => {
      if ((before?.updating?.progress === true && after?.updating === null) || (before?.creating?.progress === true && after?.creating === null)) {
        unsubscribe();
        this.$scope.$close();
      }
    });

    if (this.isFormValid()) {
      if (this.paramId) {
        this.$ngRedux.dispatch(this.customFieldActions.updateCustomField(this.$scope.customField));
      } else {
        this.$scope.customField.name = generateCustomFieldNameFromLabel(this.$scope.customField.label);
        this.$ngRedux.dispatch(this.customFieldActions.createCustomField(this.$scope.customField));
      }
    }
  };

  private addPredefinedValue = () => {
    if (this.$scope.customField.visualizationKind === "gtmhub.select") {
      const predefinedValues = this.$scope.customField.settings.predefinedValues as DropdownCustomFieldPredefinedValue[];
      const color = getDefaultPredefinedValueColor();

      predefinedValues.push({ color, value: "Option " + (predefinedValues.length + 1 < 10 ? "0" : "") + (predefinedValues.length + 1) });
    } else {
      const predefinedValues = this.$scope.customField.settings.predefinedValues as MultiSelectCustomFieldPredefinedValue[];
      predefinedValues.push({ value: "Option " + (predefinedValues.length + 1 < 10 ? "0" : "") + (predefinedValues.length + 1) });
    }
  };

  private removePredefinedValue = (index) => {
    const predefinedValues = this.$scope.customField.settings.predefinedValues as CustomFieldPredefinedValue[];
    predefinedValues.splice(index, 1);
  };

  private checkForReservedNames = (customFieldName: string): boolean => {
    return this.defaultColumns.some((column) => customFieldName === column.field);
  };

  private resetIndicator = (indicator: keyof ICustomFieldsScope["indicators"]): void => {
    this.$ngRedux.dispatch(this.customFieldActions.resetCustomFieldIndicators(indicator));
  };

  private setVisualizationKind = (visualizationKind: CustomFieldVisualizationKind): void => {
    this.$scope.customField.visualizationKind = visualizationKind;
  };

  private setTargetType = (targetType: CustomFieldTargetType): void => {
    this.$scope.customField.targetTypes[0] = targetType;
    if (!this.isSessionSpecificCompatible()) {
      this.setGlobalCustomSessionProperties();
    }
  };

  private isSessionSpecificCompatible = (): boolean => {
    return (
      this.$scope.customField.targetTypes[0] == "goal" ||
      this.$scope.customField.targetTypes[0] == "metric" ||
      this.$scope.customField.targetTypes[0] == "metric_snapshot"
    );
  };

  private isFormValid = (): boolean => {
    const isValidVisualizationKind = this.isVisualizationKindValid();
    const isSessionSpecificValid = this.isSessionSpecificValid();

    if (!isValidVisualizationKind) {
      this.$scope.showVisualizationKindOptionsErrorMessage = true;
    }

    if (!isSessionSpecificValid) {
      this.$scope.showSessionSpecificErrorMessage = true;
    }
    return isValidVisualizationKind && isSessionSpecificValid;
  };

  private isVisualizationKindValid = (): boolean => {
    const allowedVisualizationKinds = ["gtmhub.multiselect", "gtmhub.select"];
    const areSelectVisualizationKindsUsed = allowedVisualizationKinds.includes(this.$scope.customField.visualizationKind);
    const arePredefinedValuesSet = Object.values(this.$scope.customField.settings.predefinedValues || []).length !== 0;

    return !areSelectVisualizationKindsUsed || (areSelectVisualizationKindsUsed && arePredefinedValuesSet);
  };

  private isSessionSpecificValid = (): boolean => {
    const isCustomFieldSessionSpecific = this.$scope.sessionSpecific.enabled;
    const areTargetIdsSelected = (this.$scope.customField.targetIds || []).length > 0;

    return !isCustomFieldSessionSpecific || (isCustomFieldSessionSpecific && !!this.$scope.customField.targetIds && areTargetIdsSelected);
  };

  private setOperationalType = (operationalType: string): void => {
    if (operationalType === SESSION) {
      this.setSessionSpecificProperties();
    } else {
      this.setGlobalCustomSessionProperties();
    }
  };

  private setSessionSpecificProperties = (): void => {
    this.$scope.customField.operationalType = SESSION;
    this.$scope.sessionSpecific = {
      enabled: true,
    };
  };

  private setGlobalCustomSessionProperties = (): void => {
    this.$scope.sessionSpecific = {
      enabled: false,
    };
    this.$scope.customField.targetIds = [];
    this.$scope.customField.operationalType = GLOBAL;
    this.$scope.sessionTags = [];
  };

  private setTargetGroup = (targetGroup: AssigneeType): void => {
    this.$scope.customField.settings.predefinedValues = { targetGroup: targetGroup };
  };

  private clearVisualizationKindOptionsErrorMessage = (): void => {
    this.$scope.showVisualizationKindOptionsErrorMessage = false;
  };

  private clearSessionSpecificErrorMessage = (): void => {
    this.$scope.showSessionSpecificErrorMessage = false;
  };
  private countInputLength = (fieldLength: number): number => {
    return fieldLength ? fieldLength : 0;
  };

  private openSpecifySessionModal = (): void => {
    this.$rootScope.traceAction("open_specify_sessions_for_custom_field_modal", () => {
      const modalInstance = this.$uibModal.open({
        templateUrl: require("../views/specify-session-template.jade"),
        scope: this.$scope,
        windowClass: "specify-session",
      });

      modalInstance.result.then((selection: SelectedSessionsForCustomField) => {
        this.$scope.sessionListView.expanded = false;
        this.$scope.customField.operationalType = selection.customField.operationalType;
        this.$scope.customField.targetIds = selection.customField.targetIds;
        this.$scope.sessionTags = selection.sessionTags;
      });
    });
  };
}
